import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  differenceInYears,
  endOfMonth,
  isBefore,
  isValid,
  parse,
  parseISO,
} from 'date-fns';

export class FieldValidators {
  static ValidateEIN(elementValue: string) {
    const einPattern =
      /^([07][1-7]|1[0-6]|2[0-7]|[35][0-9]|[468][0-8]|9[0-589])-?\d{7}$/;
    return einPattern.test(elementValue);
  }
  static notEmpty(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return null;
      }
      const hasError = control.value?.trim() === '';
      if (hasError) {
        return {
          empty: true,
        };
      }
      return null;
    };
  }
  static emptyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasError = control.value.trim() === '';
      if (hasError) {
        return {
          empty: true,
        };
      }

      return null;
    };
  }

  /**
   * @deprecated use FieldValidators.required instead
   */
  static requiredValidator(formControlName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        const result: { [key: string]: boolean } | null = {};
        if (formControlName) {
          const cap: string =
            formControlName.charAt(0).toUpperCase() + formControlName.slice(1);
          result['required' + cap] = true;
          return result;
        }
        return { required: true };
      }
      return null;
    };
  }

  static required(name?: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors = control.errors;
      let invalid = false;

      // ***********************************************************************
      // *** Check if the control have value
      // ***********************************************************************
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        invalid = true;
      }

      if (name && invalid) {
        return {
          ...errors,
          [`${name}Required`]: true,
        };
      }

      if (invalid) {
        return {
          ...errors,
          required: true,
        };
      }

      return null;
    };
  }

  static validatePhoneNumberOrEmail(
    control: AbstractControl,
  ): ValidationErrors | null {
    const usPhoneRegex: RegExp =
      /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/;
    const patternEmail: RegExp = /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/;
    const nr = /"^[\d]*$"/;
    const lr = /"^[a-zA-Z]*$"/;

    const value = control.value as string;

    // Required detection
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value.trim() === ''
    ) {
      return {
        usernameRequired: true,
      };
    }

    // Email detection
    // If the value starts with letter or if the value includes @ and it's not
    // a valid email return invalid email
    if (
      (value.includes('@') || lr.test(value.charAt(0))) &&
      !patternEmail.test(value)
    ) {
      return {
        invalidEmail: true,
      };
    }

    // Phone detection
    // If the value starts with a + or a number, and it's not a valid phone number
    // return invalid phone
    if (
      (value.startsWith('+') || nr.test(value.charAt(0))) &&
      !usPhoneRegex.test(value)
    ) {
      return {
        invalidPhoneNumber: true,
      };
    }

    if (!usPhoneRegex.test(value) && !patternEmail.test(value)) {
      return {
        usernameInvalid: true,
      };
    }

    return null;
  }

  static mismatch(
    field: string,
    options: { errorName: string } = { errorName: 'mismatch' },
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // verify is exist error
      const match =
        control.value === control.parent?.get(field)?.value ? true : false;

      if (!match) {
        return {
          [options.errorName]: true,
        };
      }

      return null;
    };
  }

  static distinct(
    field: string,
    options: { errorName: string } = { errorName: 'distinct' },
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // verify is exist error
      const match =
        control.value === control.parent?.get(field)?.value ? true : false;

      if (match) {
        return {
          [options.errorName]: true,
        };
      }

      return null;
    };
  }

  static pattern(
    regex: RegExp,
    options: { errorName: string } = { errorName: 'pattern' },
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const valid = regex.test(control.value);

      return valid ? null : { [options.errorName]: true };
    };
  }

  static username(
    options: { errorName: string } = { errorName: 'pattern' },
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ? control.value.trim() : '';

      /*
       * In case the value is empty, return error
       */
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        value.trim() === ''
      ) {
        return {
          [options.errorName]: true,
        };
      }

      /*
       * Validate if value is a valid phone number
       */
      const phoneRegex =
        /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/;

      if (phoneRegex.test(value)) {
        return null;
      }

      /*
       * Validate if value is a valid email
       */
      const emailRegex = /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/;

      if (emailRegex.test(value)) {
        return null;
      }

      /*
       * Validate if value is a valid username
       */
      const usernameRegex = /^[a-zA-Z0-9]*$/;

      if (usernameRegex.test(value)) {
        return null;
      }

      return {
        [options.errorName]: true,
      };
    };
  }
}

export class PatternValidator {
  static pattern(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);

      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error;
    };
  }
}

export class PasswordValidators {
  static mismatch(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // verify is exist error
      const hasError =
        control.get('newPassword')?.value ===
        control.get('repeatNewPassword')?.value
          ? null
          : true;

      if (hasError) {
        // move error to repeatNewPassword field
        const errors = control.get('repeatNewPassword')?.errors || {};
        errors['mismatch'] = true;
        control.get('repeatNewPassword')?.setErrors(errors);

        return {
          mismatch: true,
        };
      }

      return null;
    };
  }

  static hasNumbers(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const numberRegex = /\d/;
      const value = control.value;

      if (!numberRegex.test(value)) {
        return {
          hasNumbers: true,
        };
      }

      return null;
    };
  }

  static hasSpecialCharacters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const specialCharRegex = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
      const value = control.value;
      if (!specialCharRegex.test(value)) {
        return {
          hasSpecialCharacters: true,
        };
      }

      return null;
    };
  }

  static hasUpperCaseLetters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const upperCaseRegex = /[A-Z]/;
      const value = control.value;

      if (!upperCaseRegex.test(value)) {
        return {
          hasUpperCaseLetters: true,
        };
      }

      return null;
    };
  }

  static hasLowerCaseLetters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const lowerCaseRegex = /[a-z]/;
      const value = control.value;

      if (!lowerCaseRegex.test(value)) {
        return {
          hasLowerCaseLetters: true,
        };
      }

      return null;
    };
  }

  static equal(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // verify is exist error
      const hasError =
        control.get('lastPassword')?.value !== control.get('newPassword')?.value
          ? null
          : true;

      if (hasError) {
        // move error to repeatNewPassword field
        const errors = control.get('newPassword')?.errors || {};
        errors['equal'] = true;
        control.get('newPassword')?.setErrors(errors);

        return {
          mismatch: true,
        };
      }

      return null;
    };
  }

  static blankValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value.length) {
        const value = control.value.replace(/\s/g, '');
        if (value === '') {
          return {
            blank: true,
          };
        }
      }

      return null;
    };
  }
}

export class DateValidators {
  static ageValidator(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        let date: Date;

        if (control.value.length === 8) {
          // parse date using datefns https://github.com/date-fns/date-fns/blob/main/docs/unicodeTokens.md
          date = parse(control.value, 'MMdyyyy', new Date());
        } else {
          date = parse(control.value, 'yyyy-MM-dd', new Date());
        }

        if (!date && !isValid(date)) {
          return { age: true };
        }

        const dif = differenceInYears(new Date(), date);

        if (dif > 120) {
          return { age: true };
        }

        if (dif < 18) {
          return { ageUnder18: true };
        }
      } catch (_) {
        return { age: true };
      }
    }
    return null;
  }

  static expirationDateValidator(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        const dateValue = control.value.split('/').join('');
        // format date MMYYYY to YYYY-MM-DD
        const month = dateValue.slice(0, 2);
        const year = dateValue.slice(2);
        // verify if it's a valid date and not expired
        const fullYear = Number(`20${year}`);
        const dateString = `${fullYear}-${month}-01`;
        const currentDate = new Date();
        const expirationDate = endOfMonth(
          parse(dateString, 'yyyy-MM-dd', new Date()),
        );
        const isValidDate = isValid(parseISO(dateString));
        if (!isValidDate) {
          return { invalidExpirationDate: true };
        }
        if (isBefore(expirationDate, currentDate)) {
          return { expired: true };
        }
      } catch (_) {
        return { invalidExpirationDate: true };
      }
    }
    return null;
  }
}

export class SSNValidator {
  // 9 digits validator
  static nineDigits(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        const ssnValue = control.value.split('-').join('');
        const invalidSSNs = [
          '000000000',
          '111111111',
          '333333333',
          '666666666',
          '123456789',
        ];

        if (invalidSSNs.includes(ssnValue)) {
          return { ssn: true };
        }

        if (ssnValue.length < 9) {
          return { nineDigits: true };
        }

        const areaNumber = parseInt(ssnValue.slice(0, 3));
        const groupNumber = parseInt(ssnValue.slice(3, 5));
        const serialNumber = parseInt(ssnValue.slice(5));
        if (
          areaNumber === 0 ||
          areaNumber === 666 ||
          areaNumber >= 900 ||
          groupNumber === 0 ||
          serialNumber === 0
        ) {
          return { ssn: true };
        }
      } catch (_) {
        return { ssn: true };
      }
    }
    return null;
  }
  // 4 digits validator
  static fourDigits(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        const ssnValue = control.value.split('-').join('');
        const invalidSSNs = ['0000'];
        if (invalidSSNs.includes(ssnValue) || ssnValue.length < 4) {
          return { lastSsnDigits: true };
        }
      } catch (_) {
        return { lastSsnDigits: true };
      }
    }
    return null;
  }
}

export class AddressValidator {
  // 9 digits validator
  static validAddress(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value) {
      try {
        if (!control.value.addressLine1) {
          return { invalidAddress: true };
        }
      } catch (_) {
        return { invalidAddress: true };
      }
    }
    return null;
  }
}

export class AmountValidator {
  // 9 digits validator
  static validAmount(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (control.value >= 0) {
      try {
        if (control.value <= 0) {
          return { invalidAmount: true };
        }
      } catch (_) {
        return { invalidAmount: true };
      }
    }
    return null;
  }
}

/**
 * Validator function for zip code field.
 * @param control The form control to validate.
 * @returns An object with a single property `invalidZipCode` set to `true`
 * if the value is invalid, or `null` if the value is valid.
 */
export function zipCodeValidator(control: AbstractControl) {
  const isValid = /\d{5}([-]\d{4})?/.test(control.value);
  return isValid ? null : { invalidZipCode: true };
}

export const FantazValidators = {
  username: {
    required: FieldValidators.required('username'),
    emailOrPhone: FieldValidators.validatePhoneNumberOrEmail,
  },
  password: {
    strong: {},
    required: Validators.required,
    minLength: Validators.minLength(8),
    hasSpecialCharacters: PasswordValidators.hasSpecialCharacters(),
    hasLowerCaseLetters: PasswordValidators.hasLowerCaseLetters(),
    hasNumbers: PasswordValidators.hasNumbers(),
    hasUpperCaseLetters: PasswordValidators.hasUpperCaseLetters(),
    blankValidator: PasswordValidators.blankValidator(),
  },

  dob: {
    required: FieldValidators.required('dob'),
    date: DateValidators.ageValidator,
    age: DateValidators.ageValidator,
  },
};
